import classNames from 'classnames'
import React, { useContext } from 'react'
import AnimateHeight from 'react-animate-height'

import { Button, ButtonProps } from '../../button/button.js'
import { ChevronDownIcon, ChevronUpIcon } from '../../icon/icon.js'
import { ModalContext } from '../../modal/modal.js'
import { Checkbox, CheckboxProps } from '../checkbox/checkbox.js'
import { ChoiceGroupProps } from '../choice-group/choice-group.js'
import { Radio, RadioProps } from '../radio/radio.js'

export interface ChooseMoreProps extends ChoiceGroupProps {
    button: ButtonProps
    isOpen?: boolean
    onToggle?: (isOpen: boolean) => void
    visibleItems?: number
    openModalValue?: string
}

export const ChooseMore = (props: ChooseMoreProps): JSX.Element => {
    const { button, label, id, isOpen, onToggle, options, type, value, visibleItems = 2 } = props

    const className: string = classNames('choose-more', isOpen && 'is-open', props.className)

    const { open } = useContext(ModalContext)

    const handleChange = (val: string, checked: boolean, onChange?: (checked: boolean) => void): void => {
        let newValue = value

        if (props.openModalValue && val === props.openModalValue && open) {
            open()
        }

        if (type === 'checkbox') {
            newValue = newValue || []

            if (Array.isArray(newValue)) {
                if (checked && !newValue.includes(val)) {
                    newValue.push(val)
                } else {
                    newValue = newValue.filter((item) => item !== val)
                }
            }
        } else {
            newValue = val
        }

        if (props.onChange) {
            props.onChange(newValue)
        }

        if (onChange) {
            onChange(checked)
        }
    }

    const isChecked = (val: string): boolean => {
        const currentValue = value

        if (type === 'checkbox' && currentValue) {
            return val === currentValue || currentValue.indexOf(val) !== -1
        } else {
            return val === currentValue
        }
    }

    const getOptionInput = (option: CheckboxProps | RadioProps): React.InputHTMLAttributes<HTMLInputElement> => {
        return {
            ...option.input,
            name: props.name,
        }
    }

    const renderChildren = (items: CheckboxProps[] | RadioProps[]) => {
        return items.map((item: CheckboxProps | RadioProps) => {
            const val = item.value || 'on'
            const onChange = (checked: boolean) => handleChange(val, checked, item.onChange)

            if (type === 'checkbox') {
                return (
                    <Checkbox
                        {...(item as CheckboxProps)}
                        checked={isChecked(item.value)}
                        className="choose-more__item"
                        input={getOptionInput(item)}
                        key={item.id}
                        onChange={onChange}
                    />
                )
            } else {
                return (
                    <Radio
                        {...(item as RadioProps)}
                        checked={isChecked(item.value)}
                        className="choose-more__item"
                        input={getOptionInput(item)}
                        key={item.id}
                        onChange={onChange}
                    />
                )
            }
        })
    }

    const handleClick = () => {
        if (onToggle) {
            onToggle(!isOpen)
        }
    }

    return (
        <fieldset className={className} id={id}>
            <legend className="choose-more__label bold">{label}</legend>
            <div className="choose-more__inner">
                {renderChildren(options.slice(0, visibleItems))}
                {options.length > visibleItems && (
                    <AnimateHeight
                        className="choose-more__content"
                        height={isOpen ? 'auto' : 0}
                        duration={150}
                        applyInlineTransitions={false}
                    >
                        <div className="choose-more__content-inner">{renderChildren(options.slice(visibleItems))}</div>
                    </AnimateHeight>
                )}
            </div>
            {options.length > visibleItems && (
                <Button
                    {...button}
                    className="choose-more__button"
                    iconRight={isOpen ? ChevronUpIcon : ChevronDownIcon}
                    isBlock="xs"
                    onClick={handleClick}
                    style="link"
                />
            )}
        </fieldset>
    )
}
